import type {
  UserEventGroup,
  UserEventGroupPatch,
} from "@app/analytics/types/eventGroupTypes";
import type { ParticipantAccessLevel, UserSettings } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

export type UpcomingEventMeetingRecapRecipient =
  | "everyone"
  | "just_me"
  | "no_one";

export const isReadInvited = (event: UserEventGroup) => event.optIn;

export const canInviteRead = (event: UserEventGroup) => {
  const isVcMeeting = event.meetingPlatform != null;
  return (event.isVcOwner || !event.isPrivate) && isVcMeeting;
};

export const canShareEvent = (event: UserEventGroup) => {
  return (
    event.predictedAccessLevel === "editor" ||
    event.predictedAccessLevel === "owner"
  );
};

export const eventIsPrivate = (
  event: UserEventGroup,
  pendingUpdates?: UserEventGroupPatch
) => {
  return pendingUpdates?.isPrivate ?? event.isPrivate ?? false;
};

export const internalParticipantAccessLevel = ({
  event,
  pendingUpdates,
  userSettings,
}: {
  event: UserEventGroup;
  pendingUpdates?: UserEventGroupPatch;
  userSettings?: UserSettings;
}) => {
  // no participants have access if the event is private
  const isPrivate = eventIsPrivate(event, pendingUpdates);
  if (isPrivate) return "no_access";

  // determine based on settings in priority order:
  //  1. pending update to event-specific override
  //  2. event-specific override
  //  3. user's default access level
  return (
    pendingUpdates?.internalParticipantAccessLevel ??
    event.internalParticipantAccessLevel ??
    userSettings?.internalParticipantAccessLevel ??
    "no_access"
  );
};

export const externalParticipantAccessLevel = ({
  event,
  pendingUpdates,
  userSettings,
}: {
  event: UserEventGroup;
  pendingUpdates?: UserEventGroupPatch;
  userSettings?: UserSettings;
}) => {
  // no participants have access if the event is private
  const isPrivate = eventIsPrivate(event, pendingUpdates);
  if (isPrivate) return "no_access";

  // determine based on settings in priority order:
  //  1. pending update to event-specific override
  //  2. event-specific override
  //  3. user's default access level
  return (
    pendingUpdates?.externalParticipantAccessLevel ??
    event.externalParticipantAccessLevel ??
    userSettings?.externalParticipantAccessLevel ??
    "no_access"
  );
};

export const roleHasAccess = (accessLevel: ParticipantAccessLevel) => {
  return accessLevel !== "no_access";
};

export const getAccessDescription = ({
  internalParticipantsRole,
  externalParticipantsRole,
}: {
  internalParticipantsRole: ParticipantAccessLevel;
  externalParticipantsRole: ParticipantAccessLevel;
}) => {
  const internalsHaveAccess = roleHasAccess(internalParticipantsRole);
  const externalsHaveAccess = roleHasAccess(externalParticipantsRole);

  if (internalsHaveAccess && externalsHaveAccess) return null;

  let thoseWithoutAccess = "";

  if (!internalsHaveAccess && !externalsHaveAccess) {
    thoseWithoutAccess = "internal and external";
  } else if (!internalsHaveAccess) {
    thoseWithoutAccess = "internal";
  } else {
    thoseWithoutAccess = "external";
  }

  return `${thoseWithoutAccess} participants have no access`;
};

export const getMeetingRecapRecipients = (
  event: UserEventGroup,
  pendingUpdates: UserEventGroupPatch,
  userSettings: UserSettings
): UpcomingEventMeetingRecapRecipient => {
  // use the settings in the priority order:
  //  1. pending update to event-specific override
  //  2. event-specific override
  //  3. user's default recap settings for email

  const meetingRecapEmailSettings =
    UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
      userSettings,
      { category: "meeting_report", provider: "email" }
    );

  const sendToMe =
    pendingUpdates.sendMeetingRecapToMe ??
    event.sendMeetingRecapToMe ??
    (meetingRecapEmailSettings?.sendTo === "me" ||
      meetingRecapEmailSettings?.sendTo === "participants_with_access");

  const sendToParticipants =
    pendingUpdates.sendMeetingRecapToParticipantsWithAccess ??
    event.sendMeetingRecapToParticipantsWithAccess ??
    meetingRecapEmailSettings?.sendTo === "participants_with_access";

  if (sendToMe && sendToParticipants) {
    return "everyone";
  }

  return sendToMe ? "just_me" : "no_one";
};
